import React, { useCallback, useEffect, useState } from 'react';
import { RevoltModal, RevoltDropdownButton } from '../../../../../components';
import RecommendedModelDownloadModal from './RecommendedModelDownloadModal';
import './HubRepoDetailModal.css';

/**
 * HubRepoDetailModal
 *
 * Opened when the user clicks a Hugging Face search hit in the Discover tab.
 * Fetches the repo file tree via the models manager (which calls the desktop
 * IPC proxy), classifies .gguf files into `single | shards | projector`, and
 * lets the user download a single-file GGUF through the existing
 * RecommendedModelDownloadModal (generalized with downloadSource='url').
 *
 * Shard groups and mmproj files are intentionally not importable yet — we
 * surface them clearly so users understand *why* (this matches the "shard
 * policy" decision: detect & warn, no merge).
 */
function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const digits = i === 0 ? 0 : i === 1 ? 1 : 2;
  return `${v.toFixed(digits)} ${units[i]}`;
}

function deriveDisplayName(repoId, fileBaseName) {
  const cleanFile = fileBaseName.replace(/\.gguf$/i, '');
  // Prefer the file's own name when it's reasonably descriptive, otherwise
  // prefix with the repo's short name so users can tell variants apart.
  if (cleanFile.length >= 6) return cleanFile;
  const shortRepo = (repoId || '').split('/').pop() || 'Model';
  return `${shortRepo} — ${cleanFile}`;
}

export default function HubRepoDetailModal({
  isOpen,
  onClose,
  repo,           // { id, author, name, likes, downloads, tags, ... }
  manager,        // DesktopModelsManager instance (used for hubListRepoFiles)
  onViewModel,    // called after successful download/import
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null); // { classification, allFiles, info }
  const [downloadSpec, setDownloadSpec] = useState(null); // { displayName, downloadUrl, ... }
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!isOpen || !repo?.id || !manager?.hubListRepoFiles) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setShowAll(false);

    manager
      .hubListRepoFiles({ repoId: repo.id })
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'Failed to list repo files');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, repo, manager]);

  const handleDownloadSingle = useCallback(
    (file) => {
      if (!file?.downloadUrl) return;
      setDownloadSpec({
        displayName: deriveDisplayName(repo?.id, file.baseName || file.path.split('/').pop()),
        downloadUrl: file.downloadUrl,
        fileSize: formatBytes(file.size),
        quantization: null,
        description: `From Hugging Face: ${repo?.id} / ${file.path}`,
        mode: 'url',
      });
    },
    [repo]
  );

  if (!isOpen) return null;

  const classification = data?.classification || { single: [], shards: [], projector: [] };
  const allFiles = data?.allFiles || [];

  return (
    <>
      <RevoltModal
        isOpen={isOpen && !downloadSpec}
        onClose={onClose}
        title={
          <div className="hub-repo-detail-title">
            <i className="fa-brands fa-hubspot" />
            {repo?.id || 'Hugging Face Repo'}
          </div>
        }
        closeOnBackdrop
        showCloseButton
        containerClassName="hub-repo-detail-modal-container"
      >
        <div className="hub-repo-detail-body">
          <div className="hub-repo-detail-body-scroll">
          <div className="hub-repo-detail-meta">
            <span>▼ {Number(repo?.downloads || 0).toLocaleString()}</span>
            <span>♥ {Number(repo?.likes || 0).toLocaleString()}</span>
            {repo?.pipelineTag ? <span>Task: {repo.pipelineTag}</span> : null}
            {repo?.gated ? <span className="hub-repo-tag-warning">Gated</span> : null}
          </div>

          {loading && (
            <div className="hub-repo-detail-loading">
              <i className="fa-solid fa-spinner fa-spin" />
              <span>Loading files from Hugging Face…</span>
            </div>
          )}

          {error && !loading && (
            <div className="hub-repo-detail-error">
              <i className="fa-solid fa-triangle-exclamation" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && data && (
            <>
              <section className="hub-repo-section">
                <div className="hub-repo-section-header">
                  <h4>Recognized GGUF files</h4>
                  <span className="hub-repo-section-count">
                    {classification.single.length}
                  </span>
                </div>
                {classification.single.length === 0 ? (
                  <p className="hub-repo-section-empty">
                    No single-file GGUFs were detected in this repo. See "All files" below.
                  </p>
                ) : (
                  <ul className="hub-repo-file-list">
                    {classification.single.map((f) => (
                      <li key={f.path} className="hub-repo-file-row">
                        <div className="hub-repo-file-info">
                          <span className="hub-repo-file-name">{f.baseName}</span>
                          <span className="hub-repo-file-sub">
                            {f.dirPath ? `${f.dirPath} · ` : ''}
                            {formatBytes(f.size)}
                          </span>
                        </div>
                        <RevoltDropdownButton
                          title="Download this GGUF and import into Revolt"
                          showDropdown={false}
                          onClick={() => handleDownloadSingle(f)}
                          className="hub-repo-download-btn"
                        >
                          <i className="fa-solid fa-download" />
                          Download
                        </RevoltDropdownButton>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {classification.shards.length > 0 && (
                <section className="hub-repo-section">
                  <div className="hub-repo-section-header">
                    <h4>Sharded (multi-part) GGUFs</h4>
                    <span className="hub-repo-section-count">
                      {classification.shards.length}
                    </span>
                  </div>
                  <p className="hub-repo-section-note">
                    Revolt does not yet support importing sharded GGUF sets as a single model.
                    These are listed for transparency.
                  </p>
                  <ul className="hub-repo-file-list">
                    {classification.shards.map((g) => (
                      <li key={g.shardKey} className="hub-repo-file-row hub-repo-file-row--disabled">
                        <div className="hub-repo-file-info">
                          <span className="hub-repo-file-name">
                            {g.parts[0]?.baseName?.replace(/-\d{5}-of-\d{5}-?/, '-*-')}
                          </span>
                          <span className="hub-repo-file-sub">
                            {g.dirPath ? `${g.dirPath} · ` : ''}
                            {g.parts.length}/{g.totalParts} parts ·
                            {' '}{formatBytes(g.parts.reduce((s, p) => s + (p.size || 0), 0))}
                          </span>
                        </div>
                        <span className="hub-repo-tag-warning">Not supported</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {classification.projector.length > 0 && (
                <section className="hub-repo-section">
                  <div className="hub-repo-section-header">
                    <h4>Multimodal projectors (mmproj)</h4>
                    <span className="hub-repo-section-count">
                      {classification.projector.length}
                    </span>
                  </div>
                  <p className="hub-repo-section-note">
                    These are projector files used alongside a base GGUF for multimodal input.
                    They are not standalone models.
                  </p>
                  <ul className="hub-repo-file-list">
                    {classification.projector.map((f) => (
                      <li key={f.path} className="hub-repo-file-row hub-repo-file-row--disabled">
                        <div className="hub-repo-file-info">
                          <span className="hub-repo-file-name">{f.baseName}</span>
                          <span className="hub-repo-file-sub">
                            {f.dirPath ? `${f.dirPath} · ` : ''}
                            {formatBytes(f.size)}
                          </span>
                        </div>
                        <span className="hub-repo-tag-warning">Projector</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="hub-repo-section">
                <div className="hub-repo-section-header">
                  <h4>All files</h4>
                  <button
                    type="button"
                    className="hub-repo-toggle-all"
                    onClick={() => setShowAll((v) => !v)}
                  >
                    {showAll ? 'Hide' : `Show (${allFiles.length})`}
                  </button>
                </div>
                {showAll && (
                  <ul className="hub-repo-file-list">
                    {allFiles.map((f) => (
                      <li key={f.path} className="hub-repo-file-row">
                        <div className="hub-repo-file-info">
                          <span className="hub-repo-file-name">{f.path}</span>
                          <span className="hub-repo-file-sub">{formatBytes(f.size)}</span>
                        </div>
                        {f.path.toLowerCase().endsWith('.gguf') ? (
                          <RevoltDropdownButton
                            title="Download this GGUF and import into Revolt"
                            showDropdown={false}
                            onClick={() =>
                              handleDownloadSingle({
                                ...f,
                                baseName: f.path.split('/').pop(),
                                dirPath: f.path.includes('/')
                                  ? f.path.slice(0, f.path.lastIndexOf('/'))
                                  : '',
                              })
                            }
                            className="hub-repo-download-btn hub-repo-download-btn--compact"
                          >
                            <i className="fa-solid fa-download" />
                            Download
                          </RevoltDropdownButton>
                        ) : (
                          <span className="hub-repo-file-sub" aria-hidden />
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
          </div>
        </div>
      </RevoltModal>

      <RecommendedModelDownloadModal
        isOpen={!!downloadSpec}
        onClose={() => setDownloadSpec(null)}
        recommendedModel={downloadSpec}
        downloadSource="url"
        titleLabel="Download from Hugging Face"
        onViewModel={(importedModel) => {
          setDownloadSpec(null);
          onClose?.();
          onViewModel?.(importedModel);
        }}
      />
    </>
  );
}
